import { applyTransformations } from "../calculation/Calculation";
import { TransformationTags } from "../calculation/TransformationTags";
import { hasFlag } from "../Flags";
import { PlayerContextState, PlayerContextTransform } from "../PlayerContext";
import { Resource } from "../Resources";
import {
  getSchoolExpByUsage,
  getSchoolLevel,
  grantSchoolExp,
  School,
} from "../spells/ElementsAndSchools";
import { registerTimeTickListener } from "../timetick/TimeTick";
import { getSecondsPlayed } from "../timetick/TotalTimePlayed";
import { DungeonFloors, getUnlockedDungeons } from "./dungeons/Dungeons";

function onTick(
  state: PlayerContextState,
  elapsedTimeSec: number,
): PlayerContextState {
  const breedingLevel = getSchoolLevel(state, School.Breeding);
  for (let dungeonFloorId in state.creatures.creatureAllocation) {
    const dungeonFloor = DungeonFloors.getById(dungeonFloorId);
    const targetLevel = dungeonFloor.getBreedingLevel();

    if (!isDungeonFloorValidForCreatures(state, dungeonFloorId)) {
      continue;
    }

    const allocatedCreatures =
      state.creatures.creatureAllocation[dungeonFloorId] || 0;
    if (allocatedCreatures > 0) {
      let lastTime = getLastRewardTimeForDungeonFloor(state, dungeonFloorId);
      // Assignment needed so it starts working
      state.creatures.lastRewardTime[dungeonFloorId] = lastTime;

      const threshold = getThresholdSec(
        state,
        breedingLevel,
        targetLevel,
        allocatedCreatures,
      );
      const thresholdMultiplier = getThresholdMultiplier(
        state,
        breedingLevel,
        targetLevel,
        allocatedCreatures,
      );
      let timesExplored = 0;
      while (getSecondsPlayed(state) - lastTime > threshold) {
        // Get a reward!
        lastTime += threshold;
        timesExplored++;
        state.creatures.lastRewardTime[dungeonFloorId] = lastTime;
        const outcome = dungeonFloor.doExplore(state);
        if (outcome != null) {
          state = outcome.breeding(state, thresholdMultiplier);
        }
      }
      if (timesExplored > 0) {
        const amountOfExp = getSchoolExpByUsage(
          state,
          School.Breeding,
          targetLevel,
          [],
        );
        state = grantSchoolExp(
          School.Breeding,
          amountOfExp * timesExplored,
        )(state);
      }
    } else {
      state.creatures.lastRewardTime[dungeonFloorId] = getSecondsPlayed(state);
    }
  }

  return state;
}

export function getCreatureMessageLog(state: PlayerContextState) {
  return state?.creatureMessageLog || [];
}

let throttleTimestamp: number = 0;
let throttleCount: number = 0;

const THROTTLE_THRESHOLD = 40;

export function wouldBeThrottledIfPushingToCreatureMessageLog(
  state: PlayerContextState,
) {
  const timestamp = Date.now();
  const timestampSec = Math.floor(timestamp / 1000);
  return (
    timestampSec == throttleTimestamp && throttleCount >= THROTTLE_THRESHOLD
  );
}

export function pushToCreatureMessageLog(
  state: PlayerContextState,
  log: string,
): PlayerContextState {
  if (!state.creatureMessageLog) {
    state.creatureMessageLog = [];
  }
  const timestamp = Date.now();
  const timestampSec = Math.floor(timestamp / 1000);
  if (throttleTimestamp == timestampSec) {
    throttleCount++;
  } else {
    throttleTimestamp = timestampSec;
    throttleCount = 1;
  }
  if (throttleCount == THROTTLE_THRESHOLD) {
    state.creatureMessageLog.unshift({
      id: Math.random().toString(),
      timestamp: getSecondsPlayed(state),
      realTimestamp: timestamp,
      message: "(More loot omitted)",
    });
    state.creatureMessageLog = state.creatureMessageLog.slice(0, 40);
  } else if (throttleCount < THROTTLE_THRESHOLD) {
    state.creatureMessageLog.unshift({
      id: Math.random().toString(),
      timestamp: getSecondsPlayed(state),
      realTimestamp: timestamp,
      message: log,
    });
    state.creatureMessageLog = state.creatureMessageLog.slice(0, 40);
  }
  return state;
}

export function isDungeonFloorValidForCreatures(
  state: PlayerContextState,
  dungeonFloorId: string,
): boolean {
  const breedingLevel = getSchoolLevel(state, School.Breeding);
  const dungeonFloor = DungeonFloors.getById(dungeonFloorId);
  const targetLevel = dungeonFloor.getBreedingLevel();

  if (breedingLevel < targetLevel) {
    return false;
  }

  if (!dungeonFloor.isUnlocked(state)) {
    return false;
  }

  if (
    !dungeonFloor.canFightBoss(state) &&
    dungeonFloor.getBoss(state) !== undefined
  ) {
    return false;
  }

  return true;
}

function getLastRewardTimeForDungeonFloor(
  state: PlayerContextState,
  dungeonFloorId: string,
): number {
  return (
    state?.creatures?.lastRewardTime?.[dungeonFloorId] ||
    getSecondsPlayed(state)
  );
}

export function getFreeCreatures(state: PlayerContextState): number {
  const creatureAllocation = state.creatures.creatureAllocation;
  let creaturesAllocated = 0;
  for (let dungeonFloor in creatureAllocation) {
    creaturesAllocated += creatureAllocation[dungeonFloor] || 0;
  }
  return state.creatures.totalCreatures - creaturesAllocated;
}

function allocateCreaturesToDungeonFloorImpl(
  dungeonFloorId: string,
  amount: number,
  state: PlayerContextState,
) {
  const currentAllocation =
    state.creatures.creatureAllocation?.[dungeonFloorId] || 0;
  const actualAmountToAllocate =
    amount > 0
      ? Math.min(getFreeCreatures(state), amount)
      : Math.max(-currentAllocation, amount);
  state.creatures.creatureAllocation[dungeonFloorId] =
    currentAllocation + actualAmountToAllocate;
  return state;
}

export function allocateCreaturesToDungeonFloor(
  this: any,
  dungeonFloorId: string,
  amount: number,
): PlayerContextTransform {
  // Do it this way to avoid creating extra functions and avoid rerenders
  return allocateCreaturesToDungeonFloorImpl.bind(this, dungeonFloorId, amount);
}

function grantNewCreatureImpl(amount: number, state: PlayerContextState) {
  state.creatures.totalCreatures = Math.max(
    0,
    Math.min(
      calculateCreatureCap(state),
      state.creatures.totalCreatures + amount,
    ),
  );
  return state;
}

export function grantNewCreature(
  this: any,
  amount: number,
): PlayerContextTransform {
  // Do it this way to avoid creating extra functions and avoid rerenders
  return grantNewCreatureImpl.bind(this, amount);
}

export function calculateCreatureCap(state: PlayerContextState): number {
  return applyTransformations(
    [TransformationTags.Creature, TransformationTags.Cap],
    state,
    0,
  );
}

export function getFloorsAvailableForCreatures(state: PlayerContextState) {
  if (!hasFlag(state, "creatures_unlocked")) {
    return [];
  }
  const dungeons = getUnlockedDungeons(state);
  const floors = dungeons
    .map((dungeon) => {
      return dungeon
        .getDungeonFloors()
        .filter((floor) =>
          isDungeonFloorValidForCreatures(state, floor.getId()),
        );
    })
    .flat();
  return floors;
}

function getAverageMonstiumGainForFloor(
  state: PlayerContextState,
  dungeonFloorId: string,
): number {
  const dungeonFloor = DungeonFloors.getById(dungeonFloorId);
  const possibilities = dungeonFloor.getExplorationPossibilities(state);
  let totalMonstiumEarned = 0,
    totalWeight = 0;
  for (let possibility of possibilities) {
    const enemy = possibility.transforms?.enemy;
    if (enemy != null) {
      const enemyMonstium = applyTransformations(
        [
          TransformationTags.Loot,
          Resource.Monstium,
          TransformationTags.FromCreatures,
        ],
        state,
        enemy.getMonstiumAwarded(state),
      );
      totalMonstiumEarned += enemyMonstium * possibility.weight;
    }
    totalWeight += possibility.weight;
  }
  return totalMonstiumEarned / totalWeight;
}

const MINIMUM_THRESHOLD = 0.01;

function getThresholdSec(
  state: PlayerContextState,
  breedingLevel: number,
  targetLevel: number,
  allocatedCreatures: number,
) {
  // Give reward once per minute per creature allocated + 10% more multiplicative rewards
  // for every 1 level above the target breeding level.
  // Example: Exploring School Basement B1 (target level 2) with a breeding level of 17,
  // with 2 creatures at the same time, results in a reward every 7.18 seconds.
  // The minimum threshold is 0.01 sec to avoid crashes.
  let threshold =
    60.0 / (allocatedCreatures * Math.pow(1.1, breedingLevel - targetLevel));
  threshold = applyTransformations(
    [TransformationTags.Creature, TransformationTags.Threshold],
    state,
    threshold,
  );
  return Math.max(MINIMUM_THRESHOLD, threshold);
}

function getThresholdMultiplier(
  state: PlayerContextState,
  breedingLevel: number,
  targetLevel: number,
  allocatedCreatures: number,
) {
  // If threshold is less than the minimum, how much less?
  let threshold =
    60.0 / (allocatedCreatures * Math.pow(1.1, breedingLevel - targetLevel));
  threshold = applyTransformations(
    [TransformationTags.Creature, TransformationTags.Threshold],
    state,
    threshold,
  );
  if (threshold > MINIMUM_THRESHOLD) {
    return 1;
  }
  return MINIMUM_THRESHOLD / threshold;
}

export function getMonstiumGainPerSecFromCreatures(
  state: PlayerContextState,
): number {
  let totalGainPerSec = 0;
  const breedingLevel = getSchoolLevel(state, School.Breeding);
  for (let dungeonFloorId in state.creatures.creatureAllocation) {
    const dungeonFloor = DungeonFloors.getById(dungeonFloorId);
    const targetLevel = dungeonFloor.getBreedingLevel();

    if (!isDungeonFloorValidForCreatures(state, dungeonFloorId)) {
      continue;
    }

    const allocatedCreatures =
      state.creatures.creatureAllocation[dungeonFloorId] || 0;
    if (allocatedCreatures > 0) {
      const threshold = getThresholdSec(
        state,
        breedingLevel,
        targetLevel,
        allocatedCreatures,
      );
      const gainPerKill = getAverageMonstiumGainForFloor(state, dungeonFloorId);
      totalGainPerSec += gainPerKill / threshold;
    }
  }

  return totalGainPerSec;
}

export function loadCreatures() {
  registerTimeTickListener("Creatures", onTick);
}
